import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ordersAPI, filesAPI, paymentsAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, Loader2, AlertCircle, Save, Upload, Download, Trash2, 
  FileImage, Clock, CheckCircle, RefreshCw, X, ImagePlus, Info, Sparkles, Plus, FileEdit, Send, CreditCard, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

// Status config - matches backend OrderStatus enum
const STATUS_CONFIG = {
  draft: { label: 'Draft', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  received: { label: 'Received', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  in_progress: { label: 'In Progress', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  delivered: { label: 'Delivered', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  canceled: { label: 'Canceled', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

// File upload states
const UPLOAD_STATES = {
  pending: 'pending',
  uploading: 'uploading',
  confirming: 'confirming',
  done: 'done',
  error: 'error',
};

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function PhotoRestorationOrder() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  // Order state
  const [order, setOrder] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [isResumingDraft, setIsResumingDraft] = useState(false);
  const [showNewOrderConfirm, setShowNewOrderConfirm] = useState(false);
  
  // Intake form state
  const [formData, setFormData] = useState({
    restoration_level: 'standard',
    colorization: false,
    output_format: 'digital',
    special_instructions: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Payment state
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [pollingPayment, setPollingPayment] = useState(false);
  const [checkoutBanner, setCheckoutBanner] = useState(null); // 'success' | 'cancel' | null
  
  // Files state
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Check for checkout return params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutResult = params.get('checkout');
    const sessionId = params.get('session_id');
    
    if (checkoutResult === 'success' && sessionId) {
      setCheckoutBanner('success');
      // Start polling for payment status
      pollPaymentStatus(sessionId);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (checkoutResult === 'cancel') {
      setCheckoutBanner('cancel');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Poll payment status after checkout
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      setPollingPayment(false);
      toast.info('Payment verification timed out. Please refresh the page.');
      return;
    }
    
    setPollingPayment(true);
    
    try {
      const status = await paymentsAPI.getStatusBySession(sessionId);
      
      if (status.payment_state === 'paid') {
        setPollingPayment(false);
        // Refresh order to get updated payment state
        if (orderId) {
          const refreshedOrder = await ordersAPI.get(orderId);
          setOrder(refreshedOrder);
        }
        toast.success('Payment confirmed!', {
          description: 'You can now submit your order.',
        });
        return;
      } else if (status.payment_state === 'failed') {
        setPollingPayment(false);
        toast.error('Payment failed', {
          description: 'Please try again.',
        });
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Payment status check failed:', err);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  // Check for existing draft or create new order on mount
  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/order/photo-restoration' } } });
      return;
    }
    
    initializeOrder();
  }, [isAuthenticated, authLoading]);

  const initializeOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check for existing draft order
      const existingOrders = await ordersAPI.list();
      const existingDraft = existingOrders.find(o => 
        o.service_type === 'photo_restoration' && 
        o.status === 'draft' && 
        o.intake_complete === false
      );
      
      if (existingDraft) {
        // Resume existing draft
        console.log('Resuming draft order:', existingDraft.id);
        setOrder(existingDraft);
        setOrderId(existingDraft.id);
        setIsResumingDraft(true);
        setFormData({
          restoration_level: existingDraft.details?.restoration_level || 'standard',
          colorization: existingDraft.details?.colorization || false,
          output_format: existingDraft.details?.output_format || 'digital',
          special_instructions: existingDraft.details?.special_instructions || '',
        });
        loadFiles(existingDraft.id);
      } else {
        // Create new order
        await createOrder();
      }
    } catch (err) {
      console.error('Failed to initialize order:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async () => {
    try {
      setCreating(true);
      setError(null);
      setIsResumingDraft(false);
      
      const newOrder = await ordersAPI.create({
        service_type: 'photo_restoration',
        title: 'Photo Restoration',
        details: {
          restoration_level: 'standard',
          colorization: false,
          output_format: 'digital',
          special_instructions: '',
        }
      });
      
      console.log('Order created:', newOrder.id);
      setOrder(newOrder);
      setOrderId(newOrder.id);
      setFormData({
        restoration_level: newOrder.details?.restoration_level || 'standard',
        colorization: newOrder.details?.colorization || false,
        output_format: newOrder.details?.output_format || 'digital',
        special_instructions: newOrder.details?.special_instructions || '',
      });
      
      // Load files for this order
      loadFiles(newOrder.id);
    } catch (err) {
      console.error('Failed to create order:', err);
      setError(err.message);
    } finally {
      setCreating(false);
      setLoading(false);
    }
  };

  const handleStartNewOrder = async () => {
    setShowNewOrderConfirm(false);
    setLoading(true);
    await createOrder();
  };

  const loadFiles = async (id) => {
    try {
      setFilesLoading(true);
      const data = await filesAPI.listForOrder(id || orderId);
      setFiles(data.filter(f => f.kind === 'reference'));
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleSaveIntake = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      
      const updatedOrder = await ordersAPI.update(orderId, {
        details: formData,
      });
      
      setOrder(updatedOrder);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save intake:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkIntakeComplete = async () => {
    try {
      setSaving(true);
      
      const updatedOrder = await ordersAPI.update(orderId, {
        details: formData,
        intake_complete: true,
      });
      
      setOrder(updatedOrder);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to mark intake complete:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle Pay & Submit - ALWAYS creates fresh checkout session and redirects immediately
  const handlePayment = async () => {
    try {
      setPaymentProcessing(true);
      console.log('[Payment] Creating fresh checkout session for order:', orderId);
      
      const { checkout_url, session_id } = await paymentsAPI.createCheckoutSession(orderId);
      
      console.log('[Payment] Checkout session created:', session_id);
      console.log('[Payment] Redirecting to checkout_url immediately...');
      
      // IMMEDIATE redirect - no delays, no storing URL
      window.location.href = checkout_url;
    } catch (err) {
      console.error('[Payment] Failed to create checkout session:', err);
      toast.error('Failed to start payment', {
        description: err.message || 'Please try again.',
      });
      setPaymentProcessing(false);
    }
  };

  // File upload handlers
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newQueue = selectedFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      state: UPLOAD_STATES.pending,
      progress: 0,
      error: null,
    }));
    setUploadQueue(prev => [...prev, ...newQueue]);
    e.target.value = '';
  };

  const processUploadQueue = useCallback(async () => {
    if (uploading || !orderId) return;
    
    const pendingItems = uploadQueue.filter(item => item.state === UPLOAD_STATES.pending);
    if (pendingItems.length === 0) return;
    
    setUploading(true);
    
    for (const item of pendingItems) {
      try {
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, state: UPLOAD_STATES.uploading, progress: 0 } : q
        ));
        
        const uploadInfo = await filesAPI.getUploadUrl(
          orderId,
          'reference',
          item.file.name,
          item.file.type,
          item.file.size
        );
        
        await filesAPI.uploadToS3(uploadInfo.upload_url, item.file, (progress) => {
          setUploadQueue(prev => prev.map(q => 
            q.id === item.id ? { ...q, progress } : q
          ));
        });
        
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, state: UPLOAD_STATES.confirming, progress: 100 } : q
        ));
        
        await filesAPI.confirmUpload(uploadInfo.file_id);
        
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, state: UPLOAD_STATES.done } : q
        ));
        
      } catch (err) {
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, state: UPLOAD_STATES.error, error: err.message } : q
        ));
      }
    }
    
    setUploading(false);
    await loadFiles(orderId);
    
    setTimeout(() => {
      setUploadQueue(prev => prev.filter(q => q.state !== UPLOAD_STATES.done));
    }, 2000);
  }, [uploadQueue, uploading, orderId]);

  useEffect(() => {
    const hasPending = uploadQueue.some(item => item.state === UPLOAD_STATES.pending);
    if (hasPending && !uploading && orderId) {
      processUploadQueue();
    }
  }, [uploadQueue, uploading, orderId, processUploadQueue]);

  const removeFromQueue = (id) => {
    setUploadQueue(prev => prev.filter(q => q.id !== id));
  };

  const retryUpload = (item) => {
    setUploadQueue(prev => prev.map(q => 
      q.id === item.id ? { ...q, state: UPLOAD_STATES.pending, error: null, progress: 0 } : q
    ));
  };

  const handleDownload = async (file) => {
    try {
      const { download_url } = await filesAPI.getDownloadUrl(orderId, file.id);
      window.open(download_url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await filesAPI.delete(file.id);
      await loadFiles(orderId);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  // Loading state
  if (loading || authLoading || creating) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin mb-4" />
        <p className="text-[#E8E4DE]/60">Creating your order...</p>
      </div>
    );
  }

  // Error state
  if (error && !order) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center text-center px-4">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h3 className="text-lg text-white mb-2">Failed to create order</h3>
        <p className="text-[#E8E4DE]/60 mb-4">{error}</p>
        <Button onClick={createOrder} className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]">
          Try Again
        </Button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[order?.status] || STATUS_CONFIG.draft;

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* New Order Confirmation Dialog */}
      <AlertDialog open={showNewOrderConfirm} onOpenChange={setShowNewOrderConfirm}>
        <AlertDialogContent className="bg-[#0f1724] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Start a New Order?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#E8E4DE]/60">
              Your existing draft will remain in your Orders list. You can return to it anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 text-white border-white/10 hover:bg-white/20">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleStartNewOrder}
              className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
            >
              Start New Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button 
            asChild
            variant="ghost" 
            className="mb-4 text-[#E8E4DE]/60 hover:text-white hover:bg-white/5 -ml-2"
          >
            <Link to="/orders">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Orders
            </Link>
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="h-6 w-6 text-[#C6A14A]" />
                <h1 className="text-2xl font-light text-white tracking-wide">
                  Photo Restoration Order
                </h1>
              </div>
              <p className="text-[#E8E4DE]/60">
                Restore and enhance your precious memories
              </p>
            </div>
            <Button 
              onClick={() => setShowNewOrderConfirm(true)}
              variant="outline"
              className="border-white/20 text-[#E8E4DE] hover:bg-white/10 hover:text-white"
              data-testid="start-new-order-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Start New Order
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Draft Order Banner */}
        {isResumingDraft && (
          <div className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg" data-testid="draft-banner">
            <FileEdit className="h-5 w-5 text-blue-400 shrink-0" />
            <div className="flex-1">
              <p className="text-blue-400 font-medium">Continuing your draft order</p>
              <p className="text-blue-400/70 text-sm">Order ID: {orderId?.slice(0, 8)}...</p>
            </div>
          </div>
        )}

        {/* SECTION A: Service Intro */}
        <Card className="bg-[#0f1724] border-white/10" data-testid="service-intro">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Info className="h-5 w-5 text-[#C6A14A]" />
              Getting Started
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-[#E8E4DE]/80">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[#C6A14A] mt-1 shrink-0" />
                <span>Upload 1-20 photos you'd like restored (JPG, PNG, WebP, or GIF)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[#C6A14A] mt-1 shrink-0" />
                <span>High-resolution scans work best for detailed restoration</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[#C6A14A] mt-1 shrink-0" />
                <span>Fill out the intake form to specify your restoration preferences</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[#C6A14A] mt-1 shrink-0" />
                <span>Click "Mark Intake Complete" when you're ready for us to begin</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Order Status Card */}
        <Card className="bg-[#0f1724] border-white/10" data-testid="order-status">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-[#E8E4DE]/60 text-sm">Order ID:</span>
                <code className="text-[#C6A14A] text-sm font-mono">{orderId?.slice(0, 8)}...</code>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#E8E4DE]/60 text-sm">Status:</span>
                <Badge className={statusConfig.className}>{statusConfig.label}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#E8E4DE]/60 text-sm">Intake:</span>
                <Badge className={order?.intake_complete 
                  ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                  : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                }>
                  {order?.intake_complete ? 'Complete' : 'In Progress'}
                </Badge>
              </div>
              {/* Payment Status Badge */}
              <div className="flex items-center gap-2">
                <span className="text-[#E8E4DE]/60 text-sm">Payment:</span>
                <Badge className={
                  order?.payment_state === 'paid' 
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : order?.payment_state === 'pending'
                    ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                    : order?.payment_state === 'failed'
                    ? 'bg-red-500/20 text-red-400 border-red-500/30'
                    : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                }>
                  {order?.payment_state === 'paid' ? 'Paid' :
                   order?.payment_state === 'pending' ? 'Pending' :
                   order?.payment_state === 'failed' ? 'Failed' : 'Unpaid'}
                </Badge>
              </div>
              {order?.created_at && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-[#E8E4DE]/40" />
                  <span className="text-[#E8E4DE]/60 text-sm">{formatDate(order.created_at)}</span>
                </div>
              )}
              {order?.submitted_at && (
                <div className="flex items-center gap-2">
                  <Send className="h-4 w-4 text-[#E8E4DE]/40" />
                  <span className="text-[#E8E4DE]/60 text-sm">Submitted: {formatDate(order.submitted_at)}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Success Banner - Shows when payment is complete and order is received */}
        {order?.payment_state === 'paid' && order?.status === 'received' && (
          <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-lg" data-testid="payment-received-banner">
            <CheckCircle className="h-5 w-5 text-green-400 shrink-0" />
            <div className="flex-1">
              <p className="text-green-400 font-medium">Payment received — order submitted.</p>
              <p className="text-green-400/70 text-sm">We'll begin processing your photos shortly.</p>
            </div>
          </div>
        )}

        {/* Checkout Result Banners */}
        {checkoutBanner === 'success' && order?.status !== 'received' && (
          <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-lg" data-testid="checkout-success-banner">
            <CheckCircle className="h-5 w-5 text-green-400 shrink-0" />
            <div className="flex-1">
              <p className="text-green-400 font-medium">Payment successful!</p>
              <p className="text-green-400/70 text-sm">
                {pollingPayment ? 'Verifying payment...' : 'Your order has been submitted.'}
              </p>
            </div>
            {pollingPayment && <Loader2 className="h-5 w-5 text-green-400 animate-spin" />}
          </div>
        )}

        {checkoutBanner === 'cancel' && (
          <div className="flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg" data-testid="checkout-cancel-banner">
            <AlertCircle className="h-5 w-5 text-yellow-400 shrink-0" />
            <div className="flex-1">
              <p className="text-yellow-400 font-medium">Payment canceled</p>
              <p className="text-yellow-400/70 text-sm">Your order is still in draft. You can try again when ready.</p>
            </div>
          </div>
        )}

        {/* Payment Section - Only show for draft orders */}
        {order?.status === 'draft' && (
          <Card className="bg-[#0f1724] border-white/10" data-testid="payment-submit-section">
            <CardContent className="pt-6">
              {/* Show different states based on payment status */}
              {order?.payment_state === 'failed' ? (
                // Payment failed - show retry option
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                      <h3 className="text-white font-medium">Payment Failed</h3>
                    </div>
                    <p className="text-red-400/80 text-sm">
                      There was an issue with your payment. Please try again.
                    </p>
                  </div>
                  <Button
                    onClick={handlePayment}
                    disabled={!order?.intake_complete || paymentProcessing}
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                    data-testid="retry-payment-btn"
                  >
                    {paymentProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Try Again
                  </Button>
                </div>
              ) : (
                // Unpaid - show Continue to Payment flow
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-medium mb-1">Ready to proceed?</h3>
                      {order?.intake_complete ? (
                        <p className="text-[#E8E4DE]/60 text-sm">
                          Price: <span className="text-[#C6A14A] font-medium">$99.00</span>
                        </p>
                      ) : (
                        <p className="text-yellow-400/80 text-sm">
                          Complete your intake form before proceeding to payment.
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={handlePayment}
                      disabled={!order?.intake_complete || paymentProcessing}
                      className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="continue-to-payment-btn"
                    >
                      {paymentProcessing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="h-4 w-4 mr-2" />
                      )}
                      Continue to Payment
                    </Button>
                  </div>
                  
                  {/* Payment info box */}
                  <div className="flex items-start gap-3 p-3 bg-[#050816]/30 rounded-lg border border-white/5">
                    <DollarSign className="h-5 w-5 text-[#C6A14A] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#E8E4DE]/60">
                      You'll be redirected to our secure payment portal (Stripe). Your order will be submitted automatically after successful payment.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* SECTION B: Intake Form */}
        <Card className="bg-[#0f1724] border-white/10" data-testid="intake-form">
          <CardHeader>
            <CardTitle className="text-lg text-white">Restoration Preferences</CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              {order?.status !== 'draft' || order?.payment_state === 'paid'
                ? 'Your order has been submitted. Preferences cannot be changed.'
                : 'Tell us how you\'d like your photos restored'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Restoration Level */}
            <div className="space-y-2">
              <Label htmlFor="restoration_level" className="text-[#E8E4DE]/80">
                Restoration Level
              </Label>
              <Select
                value={formData.restoration_level}
                onValueChange={(value) => setFormData(prev => ({ ...prev, restoration_level: value }))}
                disabled={order?.status !== 'draft' || order?.payment_state === 'paid'}
              >
                <SelectTrigger 
                  id="restoration_level"
                  className="bg-[#050816]/50 border-white/10 text-white disabled:opacity-60"
                  data-testid="restoration-level-select"
                >
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1724] border-white/10">
                  <SelectItem value="light" className="text-white hover:bg-white/10">
                    Light - Minor touch-ups and enhancements
                  </SelectItem>
                  <SelectItem value="standard" className="text-white hover:bg-white/10">
                    Standard - Typical restoration (recommended)
                  </SelectItem>
                  <SelectItem value="heavy" className="text-white hover:bg-white/10">
                    Heavy - Extensive damage repair
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Colorization */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="colorization" className="text-[#E8E4DE]/80">
                  Colorization
                </Label>
                <p className="text-sm text-[#E8E4DE]/50">
                  Add color to black & white photos
                </p>
              </div>
              <Switch
                id="colorization"
                checked={formData.colorization}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, colorization: checked }))}
                disabled={order?.status !== 'draft' || order?.payment_state === 'paid'}
                data-testid="colorization-switch"
              />
            </div>

            {/* Output Format */}
            <div className="space-y-2">
              <Label htmlFor="output_format" className="text-[#E8E4DE]/80">
                Output Format
              </Label>
              <Select
                value={formData.output_format}
                onValueChange={(value) => setFormData(prev => ({ ...prev, output_format: value }))}
                disabled={order?.status !== 'draft' || order?.payment_state === 'paid'}
              >
                <SelectTrigger 
                  id="output_format"
                  className="bg-[#050816]/50 border-white/10 text-white disabled:opacity-60"
                  data-testid="output-format-select"
                >
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1724] border-white/10">
                  <SelectItem value="digital" className="text-white hover:bg-white/10">
                    Digital only - High-res files delivered online
                  </SelectItem>
                  <SelectItem value="print" className="text-white hover:bg-white/10">
                    Print only - Physical prints shipped to you
                  </SelectItem>
                  <SelectItem value="both" className="text-white hover:bg-white/10">
                    Both - Digital files + physical prints
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Special Instructions */}
            <div className="space-y-2">
              <Label htmlFor="special_instructions" className="text-[#E8E4DE]/80">
                Special Instructions
              </Label>
              <Textarea
                id="special_instructions"
                value={formData.special_instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
                placeholder="Any specific requests? (e.g., focus on faces, remove certain elements, etc.)"
                rows={4}
                disabled={order?.status !== 'draft' || order?.payment_state === 'paid'}
                className="bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30 resize-none disabled:opacity-60"
                data-testid="special-instructions-textarea"
              />
            </div>

            {/* Save Buttons - Only show for draft orders that haven't paid */}
            {order?.status === 'draft' && order?.payment_state !== 'paid' && (
              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  onClick={handleSaveIntake}
                  disabled={saving}
                  className="bg-white/10 text-white hover:bg-white/20"
                  data-testid="save-intake-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Intake
                </Button>
                <Button
                  onClick={handleMarkIntakeComplete}
                  disabled={saving || order?.intake_complete}
                  className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                  data-testid="mark-complete-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {order?.intake_complete ? 'Intake Complete' : 'Mark Intake Complete'}
                </Button>
                {saveSuccess && (
                  <span className="flex items-center text-green-400 text-sm">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Saved!
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Requirements Card */}
        <Card className="bg-[#0f1724]/50 border-white/5" data-testid="upload-requirements">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-[#C6A14A] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#E8E4DE]">Photo Requirements</p>
                <ul className="text-sm text-[#E8E4DE]/60 mt-1 space-y-1">
                  <li>• Recommended: 1–20 photos per order</li>
                  <li>• Include full frame of the photo (no cropping)</li>
                  <li>• Avoid glare and reflections when photographing prints</li>
                  <li>• High-resolution scans work best for detailed restoration</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SECTION C: Upload Manager */}
        <Card className="bg-[#0f1724] border-white/10" data-testid="upload-manager">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <ImagePlus className="h-5 w-5 text-[#C6A14A]" />
                  Reference Photos
                </CardTitle>
                <CardDescription className="text-[#E8E4DE]/60">
                  {['in_progress', 'delivered', 'canceled'].includes(order?.status)
                    ? 'Uploads are locked once processing has begun'
                    : 'Upload the photos you\'d like restored'
                  }
                </CardDescription>
              </div>
              {!['in_progress', 'delivered', 'canceled'].includes(order?.status) ? (
                <div className="relative">
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="file-upload-input"
                  />
                  <Button className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]">
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Photos
                  </Button>
                </div>
              ) : (
                <Button disabled className="bg-gray-500/20 text-gray-400 cursor-not-allowed">
                  <Upload className="mr-2 h-4 w-4" />
                  Uploads Locked
                </Button>
              )}
            </div>
            {['in_progress', 'delivered', 'canceled'].includes(order?.status) && (
              <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-400/80">
                  Uploads are locked once processing has begun. Contact support if you need to add files.
                </p>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {/* Upload Helper Text */}
            {!['in_progress', 'delivered', 'canceled'].includes(order?.status) && (
              <div className="mb-4 p-3 bg-[#050816]/30 rounded-lg border border-white/5">
                <p className="text-sm text-[#E8E4DE]/60">
                  <span className="text-[#C6A14A] font-medium">Allowed:</span> JPG, PNG, WebP, GIF
                  <span className="mx-2">•</span>
                  <span className="text-[#C6A14A] font-medium">Recommended:</span> 1-20 photos
                  <span className="mx-2">•</span>
                  <span className="text-[#C6A14A] font-medium">Max size:</span> 50MB per file
                </p>
              </div>
            )}

            {/* Upload Queue */}
            {uploadQueue.length > 0 && (
              <div className="space-y-2 mb-4">
                {uploadQueue.map((item) => (
                  <div 
                    key={item.id}
                    className="flex items-center gap-3 p-3 bg-[#050816]/50 rounded-lg"
                  >
                    <FileImage className="h-5 w-5 text-[#C6A14A] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{item.file.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {item.state === UPLOAD_STATES.uploading && (
                          <>
                            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#C6A14A] transition-all duration-300"
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-[#E8E4DE]/60">{item.progress}%</span>
                          </>
                        )}
                        {item.state === UPLOAD_STATES.confirming && (
                          <span className="text-xs text-[#C6A14A] flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Confirming...
                          </span>
                        )}
                        {item.state === UPLOAD_STATES.done && (
                          <span className="text-xs text-green-400 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Uploaded
                          </span>
                        )}
                        {item.state === UPLOAD_STATES.error && (
                          <span className="text-xs text-red-400">{item.error}</span>
                        )}
                      </div>
                    </div>
                    {item.state === UPLOAD_STATES.error && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => retryUpload(item)}
                        className="text-[#C6A14A] hover:text-[#D4AF5A] hover:bg-white/5"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}
                    {(item.state === UPLOAD_STATES.pending || item.state === UPLOAD_STATES.error) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromQueue(item.id)}
                        className="text-[#E8E4DE]/40 hover:text-red-400 hover:bg-white/5"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* File List */}
            {filesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 text-[#C6A14A] animate-spin" />
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-white/10 rounded-lg">
                <FileImage className="h-10 w-10 text-[#E8E4DE]/30 mx-auto mb-3" />
                <p className="text-[#E8E4DE]/60">No photos uploaded yet</p>
                <p className="text-sm text-[#E8E4DE]/40 mt-1">
                  Upload your photos to get started
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {files.map((file) => (
                  <div 
                    key={file.id}
                    className="flex items-center gap-3 p-3 bg-[#050816]/30 rounded-lg group"
                  >
                    <FileImage className="h-5 w-5 text-[#C6A14A] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{file.original_filename}</p>
                      <p className="text-xs text-[#E8E4DE]/40">
                        {formatFileSize(file.size_bytes)} • {formatDate(file.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownload(file)}
                        className="text-[#C6A14A] hover:text-[#D4AF5A] hover:bg-white/5"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {!['in_progress', 'delivered', 'canceled'].includes(order?.status) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(file)}
                          className="text-[#E8E4DE]/40 hover:text-red-400 hover:bg-white/5"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <p className="text-xs text-[#E8E4DE]/40 mt-2">
                  {files.length} file{files.length !== 1 ? 's' : ''} uploaded
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
